import { v4 as uuidv4 } from "uuid";
import {
  ref,
  onMounted,
  onUnmounted,
  defineComponent,
} from "@vue/composition-api";

export interface AutocompleteResult {
  label: string;
  id: string;
}

const emptyResult: AutocompleteResult = {
  label: "",
  id: "",
};

export default defineComponent({
  name: "autcompleteId",
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [] as AutocompleteResult[],
    },
    acResult: {
      type: Object,
      required: false,
      default: () => {},
    },
    inputLabel: {
      type: String,
      required: false,
      default: "",
    },
    helpTextLabel: {
      type: String,
      required: false,
      default: "",
    },
  },

  setup(props, ctx) {
    const currentResult = ref(emptyResult);
    const arrowCounter = ref(-1);
    const browserId = ref("");
    const isOpen = ref(false);
    let results = ref<AutocompleteResult[]>([]);
    const acInput = ref("");

    acInput.value = props.acResult.label;
    browserId.value = `ac-id-${uuidv4()}`;

    const handleClickOutside = (evt: any) => {
      if (!ctx.root.$el.contains(evt.target)) {
        isOpen.value = false;
        arrowCounter.value = -1;
      }
    };

    const onArrowDown = () => {
      if (arrowCounter.value < results.value.length) {
        arrowCounter.value = arrowCounter.value + 1;
      }
    };

    const onArrowUp = () => {
      if (arrowCounter.value > 0) {
        arrowCounter.value = arrowCounter.value - 1;
      }
    };

    const onEnter = (evt: any) => {
      evt.preventDefault();
      let result: AutocompleteResult;
      if (arrowCounter.value === -1) {
        if (results.value.length > 0) {
          result = results.value[0];
        } else {
          result = emptyResult;
        }
      } else {
        result = results.value[arrowCounter.value];
      }

      setResult(result);
      isOpen.value = false;
      arrowCounter.value = -1;
    };

    const onChange = () => {
      isOpen.value = true;
      filterResults();
    };

    const onTab = () => {
      acInput.value = currentResult.value.label;
      isOpen.value = false;
    };

    const filterResults = () => {
      ctx.root.$nextTick(() => {
        results.value = (props.items as AutocompleteResult[]).filter(
          (item: AutocompleteResult) => {
            return (
              item.label.toLowerCase().indexOf(acInput.value.toLowerCase()) > -1
            );
          },
        );
        arrowCounter.value = 0;
      });
    };

    const setResultParent = (result: AutocompleteResult) => {
      currentResult.value = result;
      acInput.value = result.label;
    };

    const setFocusParent = () => {
      // if next tick is necessary, the parent is supposed to take care of that
      const el = document.getElementById(browserId.value);
      if (el) {
        el.focus();
      }
    };

    const setResult = (result: AutocompleteResult) => {
      currentResult.value = result;
      acInput.value = result.label;
      ctx.emit("update:acResult", currentResult.value);
      isOpen.value = false;

      // move the focus to the input element
      const el = document.getElementById(browserId.value);
      if (el) {
        el.focus();
      }
    };

    const toggleResults = (evt: any) => {
      evt.preventDefault();
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        results.value = props.items as AutocompleteResult[];
      }
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      arrowCounter,
      currentResult,
      browserId,
      isOpen,
      results,
      acInput,

      onArrowDown,
      onArrowUp,
      onEnter,
      onChange,
      onTab,
      toggleResults,
      setResult,

      setResultParent,
      setFocusParent,
    };
  },
});
